import React, { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { Group } from 'models/company.model'

interface GroupSelectProps {
  label: string
  groups: Group[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
}

const GroupSelect = React.forwardRef<HTMLButtonElement, GroupSelectProps>(
  (
    {
      label,
      groups,
      value,
      onChange,
      placeholder = 'Selecciona un grupo',
      required = false,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const labelClass = classNames('font-semibold', 'mb-2', 'text-sm')
    const selectButtonClass = classNames(
      'rounded-lg',
      'p-3',
      'w-full',
      'border',
      'border-gray-300',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:border-transparent',
      'outline-none',
      'bg-white',
      'cursor-pointer',
      'flex',
      'justify-between',
      'items-center',
    )

    const dropdownClass = classNames(
      'absolute',
      'w-full',
      'mt-1',
      'bg-white',
      'border',
      'border-gray-300',
      'rounded-lg',
      'shadow-lg',
      'max-h-60',
      'overflow-y-auto',
      'z-10',
    )

    const selectedGroup = groups
      .flatMap(g => g.subgroups)
      .find(sg => sg.uuid === value)

    return (
      <div className="flex flex-col mb-4 relative" ref={dropdownRef}>
        <label className={labelClass}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <button
          ref={ref}
          type="button"
          className={selectButtonClass}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={value ? 'text-black' : 'text-gray-400'}>
            {selectedGroup ? selectedGroup.name : placeholder}
          </span>
          <svg
            className={`w-5 h-5 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && (
          <div className={dropdownClass}>
            {groups.map(group => (
              <div key={group.uuid}>
                {group.subgroups.map(subgroup => (
                  <button
                    key={subgroup.uuid}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors"
                    onClick={() => {
                      onChange(subgroup.uuid)
                      setIsOpen(false)
                    }}
                  >
                    <div className="font-medium">{subgroup.name}</div>
                    {group.name !== subgroup.name && (
                      <div className="text-xs text-gray-500">{group.name}</div>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  },
)

GroupSelect.displayName = 'GroupSelect'

export default GroupSelect
